/**
 * Webmail sign-in: the mock behind `POST /domains/{domain}/accounts/{email}/sso`.
 *
 * TEMPORARY, like the seed beside it. Mints a single-use credential the browser posts as a form
 * (see {@link MailboxSso} and the page's `openWebmail`). When the endpoint lands, this is the
 * function the fetch replaces; the form-building side is already real.
 */
import type { MailboxError, MailboxRecord, MailboxSso } from './mailbox-records';

/**
 * Where the mock's form posts: the reserved example domain, NOT the provider's real login page, so
 * a click in dev never sends a made-up credential to a real third party. The real answer names the
 * provider's own URL.
 */
const MOCK_SSO_LOGIN_URL = 'https://webmail.example.com/?_task=login&_action=login';

/** How long the mock SSO call takes: long enough that the tab opened before it is seen blank. */
export const MAILBOX_SSO_MOCK_DELAY_MS = 300;

/** A 32-hex-character token, the shape the service mints. */
const mockToken = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (n) => n.toString(16).padStart(2, '0')).join('');

/**
 * One sign-in, as the endpoint would answer, after {@link MAILBOX_SSO_MOCK_DELAY_MS}. Rejects for a
 * `deleted` mailbox with the {@link MailboxError} the real service's `409` carries (the button is
 * disabled there too, so this is the backstop, not the path).
 */
export const requestMockMailboxSso = (mailbox: Pick<MailboxRecord, 'email' | 'status'>): Promise<MailboxSso> =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (mailbox.status === 'deleted') {
        const error: MailboxError = {
          code: 'conflict',
          message: `mailbox ${mailbox.email} is deleted; single sign-on is not available for it`,
        };
        reject(error);
        return;
      }
      resolve({
        email: mailbox.email,
        loginUrl: MOCK_SSO_LOGIN_URL,
        usernameField: '_user',
        passwordField: '_pass',
        token: mockToken(),
        expiresInHours: 24,
        singleUse: true,
      });
    }, MAILBOX_SSO_MOCK_DELAY_MS);
  });
